import { runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { ButtonNavigation, IButtonNavigationItem, Spinner, useAnalytics } from 'telia-front-react';
import MovieRentalCatalogStore, { MovieCategoriesParams } from '../stores/movierental/MovieRentalCatalogStore';
import RouteStore from '../stores/RouteStore';
import { TranslationsContext } from '@teliaee/telia-translate';

export interface TvCategoriesProps {
  rootCategoryId: string;
  type?: string;
  vodType?: string;
  packetIds?: string[];
  categoryIds?: string[];
  categoryId?: string;
  applicationId?: string;
  onCategoryChange?: (category: string) => void;
  isLoading?: boolean;
  spinner?: boolean;
  movieRentalCatalogStore?: MovieRentalCatalogStore;
  routeStore?: RouteStore;
  searchInput?: string;
}

const TvCategories = (props: TvCategoriesProps): JSX.Element => {
  const { translate } = React.useContext(TranslationsContext);
  const { push } = useAnalytics();
  const {
    rootCategoryId,
    type,
    vodType,
    packetIds,
    searchInput,
    categoryIds,
    applicationId,
    onCategoryChange,
    isLoading,
    spinner,
    movieRentalCatalogStore,
    routeStore,
  } = props;
  const [selectedCategory, setSelectedCategory] = React.useState<string>(props.categoryId || rootCategoryId);
  const [movieCategoriesParams, setMovieCategoriesParams] = React.useState<MovieCategoriesParams>({
    rootCategoryId: null,
    type: null,
    vodType: null,
    packetIds: null,
    categoryId: null,
    applicationId: null,
  });

  React.useEffect(() => {
    updateParams();
    movieRentalCatalogStore.loadMovieCategories(getMovieCategoriesParams());
    setSelectedCategoryFromPath();
  }, []);

  React.useEffect(() => {
    handleTypeChange();
  }, [movieCategoriesParams.type]);

  React.useEffect(() => {
    setSelectedCategory(movieCategoriesParams.rootCategoryId);
  }, [movieCategoriesParams.rootCategoryId]);

  React.useEffect(() => {
    setSelectedCategory(movieCategoriesParams.categoryId);
  }, [movieCategoriesParams.categoryId]);

  React.useEffect(() => {
    updateParams();
  }, [rootCategoryId, type, vodType, packetIds, props.categoryId]);

  const updateParams = () => {
    runInAction(() => {
      setMovieCategoriesParams(getMovieCategoriesParams());
    });
  };

  const getAllCategories = (): IButtonNavigationItem => {
    return {
      id: movieCategoriesParams.rootCategoryId,
      url: '#',
      label: translate('movieRentalSearch.allCategories'),
    };
  };

  const handleTypeChange = async () => {
    await movieRentalCatalogStore.loadMovieCategories(movieCategoriesParams);
    if (!!searchInput && searchInput.length >= 2) {
      movieRentalCatalogStore.loadSearchResults(searchInput, selectedCategory, type, categoryIds);
    }
  };

  const categoryExists = (categoryId: string) => {
    return !!movieRentalCatalogStore.getCategories(movieCategoriesParams).find((category) => Number(category.id) === Number(categoryId));
  };

  const setSelectedCategoryFromPath = () => {
    const categoryId = routeStore.decodedParams.movieRentalCategoryId;
    if (categoryId && categoryExists(categoryId)) {
      setSelectedCategory(categoryId);
      onCategoryChange(categoryId);
    }
  };

  const getMovieCategoriesParams = (): MovieCategoriesParams => {
    return {
      rootCategoryId,
      type,
      vodType,
      packetIds,
      categoryId: props.categoryId,
      applicationId,
    };
  };

  const getFilteredCategories = (): IButtonNavigationItem[] => {
    const categories: IButtonNavigationItem[] = [getAllCategories()].concat(movieRentalCatalogStore.getCategories(movieCategoriesParams));
    return categories.filter((category) => !categoryIds?.length || categoryIds.includes(`${category.id}`));
  };

  const prepareMovieCategories = (): IButtonNavigationItem[] => {
    return getFilteredCategories().map((category) => ({
      ...category,
      onClick: (e: React.MouseEvent) => categoryOnClick(e, category.id, category.label),
      current: Number(selectedCategory) === Number(category.id),
    }));
  };

  const categoryOnClick = (e: React.MouseEvent, categoryId: string, categoryName: string) => {
    e.preventDefault();
    runInAction(() => {
      setSelectedCategory(categoryId);
      onCategoryChange(categoryId);
      push(`Link - ${categoryName}`);
    });
  };

  if (
    movieRentalCatalogStore.isFetchingCategories({
      ...movieCategoriesParams,
    }) &&
    spinner
  ) {
    return <Spinner />;
  }

  if (
    isLoading ||
    movieRentalCatalogStore.isFetchingCategories(movieCategoriesParams) ||
    movieRentalCatalogStore.getCategories(movieCategoriesParams).length <= 1
  ) {
    return null;
  }

  return <ButtonNavigation items={prepareMovieCategories()} size="small" />;
};

export default inject('movieRentalCatalogStore', 'routeStore')(observer(TvCategories));
